import React, {useState, useMemo, createContext, useContext} from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import CloseButton from "../CloseButton/CloseButton";
import Hidden from "../../../Layout/components/Hidden/Hidden";
import createResponsivePropType from "../../../Layout/utils/createResponsivePropType";
import mediaQueries from "../../../Layout/utils/mediaQueries";
import mapResponsiveProp from "../../../Layout/utils/mapResponsiveProp";
import responsiveRangeProps from "../../../Layout/components/Hidden/responsiveRangeProps";

const DrawerContext = createContext({});

const sizeMap = {
    small: "300px",
    normal: "360px",
};

const DrawerCloseButton = styled(CloseButton)`
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 3;
`;

const Backdrop = styled.div`
    position: ${({cssPosition}) => cssPosition};
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: rgba(83, 99, 116, 0.4);
    pointer-events: ${({isOpen}) => (isOpen ? "all" : "none")};
    opacity: ${({isOpen}) => (isOpen ? 1 : 0)};
    transition: 0.2s ease opacity;
    will-change: opacity;
    z-index: 98;
`;

const Scroll = styled.div`
    height: ${({height}) => height};
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
`;

const DrawerModal = styled.div`
    position: absolute;
    top: 0;
    ${({side}) => `${side}: 0;`}
    bottom: 0;
    width: 95%;
    background-color: white;
    transition: 0.2s cubic-bezier(0.63, 0.3, 0.46, 0.99) transform;
    pointer-events: all;
    will-change: transform;
    z-index: 99;

    ${({size}) => {
        if (!size) return size;
        const sizes = mapResponsiveProp(size, sizeMap);
        // we want the first item in the array to always be 95%
        sizes.shift();
        sizes.unshift("95%");

        return mediaQueries({width: sizes, minWidth: sizes});
    }}

    ${({dynamicStyles: {position, boxShadow, transform}}) =>
        mediaQueries({position, transform, boxShadow})}
`;

const Footer = styled.div`
    height: 70px;
    border-top: 1px solid var(--solitude);
`;

export const DrawerFooter = ({children}) => {
    return <Footer>{children}</Footer>;
};

export function DrawerScroll({children}) {
    const {hasFooter} = useContext(DrawerContext);
    let height;
    if (hasFooter) {
        height = "calc(100% - 70px)";
    } else {
        height = "100%";
    }
    return <Scroll height={height}>{children}</Scroll>;
}

export const mapDisplayProp = ({values, modalValue, staticValue}) => {
    const [staticOnMobile, staticOnTablet, staticOnDesktop, staticOnLargeDesktop] = values;
    const value = [
        staticOnMobile ? staticValue : modalValue,
        staticOnTablet ? staticValue : modalValue,
        staticOnDesktop ? staticValue : modalValue,
        staticOnLargeDesktop ? staticValue : modalValue,
    ];
    return value;
};

Drawer.defaultProps = {
    size: "normal",
    side: "right",
    position: "absolute",
};

Drawer.propTypes = {
    size: createResponsivePropType(["small", "normal"]),
    side: PropTypes.string,
    position: PropTypes.string,
};

export default function Drawer({
    children,
    isOpen,
    onDismiss,
    size,
    position: cssPosition,
    side,
    staticAbove,
    id,
    footer,
    role,
}) {
    const hasFooter = !!footer;
    const contextValue = useMemo(() => ({hasFooter}), [hasFooter]);
    const styles = useMemo(
        () => {
            const transformValue = isOpen
                ? "none"
                : `translate3d(${side === "left" ? "-" : ""}102%, 0, 0)`;
            const boxShadowValue = `${side === "left" ? "" : "-"}2px 0 6px rgba(70, 70, 70, 0.07)`;
            if (!staticAbove) {
                return {
                    position: cssPosition,
                    transform: transformValue,
                    boxShadow: boxShadowValue,
                };
            }

            // We have to turn off certain CSS properties when staticAbove applies in order
            // to make the Drawer behave like a static element on the page instead of a modal.
            // Here we use simmilar functionality to that of Hidden where you can pass in a
            // prop staticAbove="tablet" and we then have to turn that in to a bunch of responsive rules
            // eg. @media (min-width: 768px) {position: static;}, and the possible rules themselves
            // are also dynamic and come from props.

            const responsiveValues = responsiveRangeProps({above: staticAbove});
            const position = mapDisplayProp({
                values: responsiveValues,
                modalValue: cssPosition,
                staticValue: "static",
            });
            const transform = mapDisplayProp({
                values: responsiveValues,
                modalValue: transformValue,
                staticValue: "none",
            });
            const boxShadow = mapDisplayProp({
                values: responsiveValues,
                modalValue: boxShadowValue,
                staticValue: "none",
            });
            return {
                position,
                transform,
                boxShadow,
            };
        },
        [isOpen, side, staticAbove, cssPosition],
    );

    // When using TouchableOpacity to open the drawer it dispatches multiple pointer events.
    // If we instantly render the backdrop and make it clickable, the delayed second pointer event
    // "clicks" the backdrop and instantly dismisses the drawer. We delay this by preventing
    // clicks on the backgrop until the animation is over.
    const [isAnimationFinished, setIsAnimationFinished] = useState(false);
    const onClickBackdrop = () => {
        if (!isAnimationFinished || typeof onDismiss !== "function") return;
        onDismiss();
    };

    return (
        <DrawerContext.Provider value={contextValue}>
            <Hidden above={staticAbove}>
                <Backdrop
                    cssPosition={cssPosition}
                    data-test-id="drawer-backdrop"
                    isOpen={isOpen}
                    onClick={onClickBackdrop}
                />
            </Hidden>
            <DrawerModal
                role={role}
                id={id}
                aria-expanded={isOpen}
                staticAbove={staticAbove}
                side={side}
                size={size}
                onTransitionEnd={() => setIsAnimationFinished(isOpen)}
                data-test-id="drawer-slider"
                isOpen={isOpen}
                dynamicStyles={styles}
            >
                {onDismiss && (
                    <Hidden above={staticAbove}>
                        <DrawerCloseButton size="sm" onClick={onDismiss} />
                    </Hidden>
                )}
                {children}
                {footer ? <Footer>{footer}</Footer> : null}
            </DrawerModal>
        </DrawerContext.Provider>
    );
}
